import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';

interface CalendlyConfig {
  url: string;
}

const DEFAULT_CALENDLY_CONFIG: CalendlyConfig = {
  url: ''
};

interface CalendlyConfigStore {
  config: CalendlyConfig;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  setConfig: (config: Partial<CalendlyConfig>, chatbotPublicId?: string) => Promise<boolean>;
  initializeConfig: (chatbotPublicId: string) => Promise<void>;
}

export const useCalendlyConfig = create<CalendlyConfigStore>((set) => ({
  config: DEFAULT_CALENDLY_CONFIG,
  loading: false,
  error: null,
  initialized: false,

  setConfig: async (newConfig, chatbotPublicId) => {
    if (!chatbotPublicId) return false;

    try {
      set({ loading: true, error: null });
      
      // Update using the centralized config access
      const success = await updateChatbotConfigSection(
        'calendly_config',
        newConfig,
        chatbotPublicId
      );

      if (success) {
        // Merge with existing config
        set(state => ({ 
          config: { ...state.config, ...newConfig },
          loading: false,
          error: null
        }));
        return true;
      } else {
        throw new Error('Failed to update Calendly config');
      }
    } catch (err) {
      console.error('Error saving Calendly config:', err);
      set({ 
        loading: false, 
        error: err instanceof Error ? err.message : 'Failed to update Calendly config'
      });
      return false;
    }
  },

  initializeConfig: async (chatbotPublicId) => {
    if (!chatbotPublicId) {
      set({
        error: 'Chatbot ID is required',
        loading: false
      });
      return;
    }

    try {
      set({ loading: true, error: null });

      // Get Calendly config from the centralized config
      const calendlyConfig = await getChatbotConfigSection<CalendlyConfig>(
        'calendly_config',
        chatbotPublicId
      );

      if (calendlyConfig) {
        set({ 
          config: { 
            ...DEFAULT_CALENDLY_CONFIG,
            ...calendlyConfig 
          },
          initialized: true,
          loading: false,
          error: null
        });
      } else {
        // Use default config if none is found
        set({ 
          config: DEFAULT_CALENDLY_CONFIG,
          initialized: true,
          loading: false,
          error: null
        });
      }
    } catch (err) {
      console.error('Error loading Calendly config:', err);
      set({ 
        config: DEFAULT_CALENDLY_CONFIG,
        initialized: true,
        loading: false,
        error: err instanceof Error ? err.message : 'Error loading Calendly config'
      });
    }
  }
}));